// @ts-nocheck
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { handleFooterQuickLinkClick } from '@way/theme-host/shared/utils/footer-quick-links.util';

export interface FooterLink {
  label: string;
  link: string;
}

export interface SocialLink {
  platform: string;
  url: string;
  icon: string;
}

/**
 * Footer component for the Champs theme page that displays branding, quick navigation links, social media links, and copyright information.
 * @component
 * @selector app-champs-page-footer
 * @example
 *   <app-champs-page-footer
 *     [logo]="{ text: 'Way', image: 'assets/logo.png' }"
 *     [quickLinks]="quickLinksArray"
 *     [quickLinksTitle]="'Quick links'"
 *     [socialLinks]="socialLinksArray"
 *     [socialLinksTitle]="'Connect with us'"
 *     [copyright]="`Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`"
 *     [backgroundColor]="'#ffffff'">
 *   </app-champs-page-footer>
 * @input {{ text?: string; image?: string }} logo - Optional logo object containing text and/or an image used for branding in the footer.
 * @input {FooterLink[]} quickLinks - Array of footer navigation items displayed under the quick links section; defaults to a set of common site links (Home, Express Washes, Coupons, Memberships, Express Details, Fleets).
 * @input {string} quickLinksTitle - Title text rendered above the quick links column; defaults to 'Quick links'.
 * @input {SocialLink[]} socialLinks - Array of social link objects (platform, url, icon) displayed as social icons; defaults include Instagram, Facebook, YouTube, LinkedIn, and X.
 * @input {string} socialLinksTitle - Title text rendered above the social links column; defaults to 'Connect with us'.
 * @input {string} copyright - Copyright text displayed in the footer; defaults to `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`.
 * @input {string} backgroundColor - Optional background color used to style the footer container; when omitted, the component falls back to theme or stylesheet defaults.
 * @output {none} - This component does not emit any output events.
 *
 * Purpose:
 * - Provide a reusable, presentation-focused footer for the Champs theme that is configurable via inputs for branding, navigation, social links, and visual styling.
 *
 * Key behaviors:
 * - Renders a logo area that can show either text, an image, or both.
 * - Renders configurable quick links and social links sections with their respective titles.
 * - Supports simple visual customization via the backgroundColor input.
 *
 * Lifecycle and methods:
 * - The component is template-driven and does not implement custom lifecycle hooks (no OnInit/OnDestroy explicitly implemented) or public methods in its class; it relies on inputs for configuration and native link behavior for navigation.
 */
@Component({
  selector: 'app-champs-page-footer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './footer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./footer.component.scss'],
})
export class ChampsPageFooterComponent {
  @Input() logo?: {
    text?: string;
    image?: string;
  };

  @Input() quickLinks: FooterLink[] = [
    { label: 'Home', link: '#home' },
    { label: 'Express Washes', link: '#express-wash-packages' },
    { label: 'Coupons', link: '#car-wash-coupons--way' },
    { label: 'Memberships', link: '#memberships' },
    { label: 'Express Details', link: '#express-auto-details' },
    { label: 'Fleets', link: '#fleet-management' },
  ];

  @Input() quickLinksTitle = 'Quick links';

  @Input() socialLinks: SocialLink[] = [
    { platform: 'Instagram', url: '#', icon: 'instagram' },
    { platform: 'Facebook', url: '#', icon: 'facebook' },
    { platform: 'YouTube', url: '#', icon: 'youtube' },
    { platform: 'LinkedIn', url: '#', icon: 'linkedin' },
    { platform: 'X', url: '#', icon: 'x' },
  ];

  @Input() socialLinksTitle = 'Connect with us';

  @Input() copyright = `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;

  @Input() backgroundColor?: string;
  @Input() deviceType: 'desktop' | 'tablet' | 'mobile' = 'desktop';
  @Output() quickLinkClick = new EventEmitter<string>();

  onQuickLinkClick(event: Event, link: FooterLink): void {
    handleFooterQuickLinkClick(event, link, pageType => this.quickLinkClick.emit(pageType));
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
